// src/app/layout/NavList.tsx
import { Fragment } from 'react';
import { NavLink } from 'react-router-dom';
import { NAV_ITEMS } from './nav-items';
import { useAuthStore } from '@vanialabs/core-auth';
import { hasPermission } from '@vanialabs/core-permissions';
import { useUserContextStore } from '../features/auth/state/userContext.store';

// Single rendering path for NAV_ITEMS — consumed by both the desktop
// Sidebar and the MobileNavDrawer, so there is exactly one place that
// walks the array, groups by section, and applies active styling.
// NAV_ITEMS in ./nav-items.ts remains the only source of menu data;
// this component only renders it. Behavior/markup is unchanged from
// what previously lived inline in Sidebar.tsx.

interface NavListProps {
  /**
   * Called after a nav item is clicked. Used by MobileNavDrawer to
   * close itself on navigation. Desktop Sidebar doesn't pass this.
   */
  onItemSelect?: () => void;
}

export function NavList({ onItemSelect }: NavListProps) {
  let lastSection: string | undefined;

  return (
    <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-4">
      {NAV_ITEMS.map((item) => {
        const showSectionLabel = Boolean(item.section) && item.section !== lastSection;
        lastSection = item.section;
        const Icon = item.icon;

        return (
          <Fragment key={item.to}>
            {showSectionLabel && (
              <span className="mt-3 px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--vania-colors-text-secondary,#6b7280)] first:mt-0">
                {item.section}
              </span>
            )}
            <NavLink
              to={item.to}
              onClick={onItemSelect}
              className={({ isActive }) =>
                [
                  'flex items-center gap-2.5 rounded-lg border-s-2 px-3.5 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-[var(--vania-accent-violet,#7c6cff)] bg-[var(--vania-colors-surface-secondary,rgba(255,255,255,0.06))] text-[var(--vania-colors-text-primary,#f5f6fa)]'
                    : 'border-transparent text-[var(--vania-colors-text-secondary,#a3aab8)] hover:bg-[var(--vania-colors-surface-secondary,rgba(255,255,255,0.03))]',
                ].join(' ')
              }
            >
              <Icon size={17} strokeWidth={2.2} />
              {item.label}
            </NavLink>
          </Fragment>
        );
      })}
    </nav>
  );
}
